import { Button, LinkButton } from './primitives/Button.js';
import { Card, Container } from './primitives/Misc.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';
import { TIERS, formatAmount } from '../data/plans.js';
import { PWA_URL } from '../config.js';
import './Pricing.css';

export interface PricingProps {
  ccyCode: CurrencyCode;
  ccySymbol: string;
  ccyName: string;
  taxLabel: string;
  ccyNote: string;
  onCycleCurrency: () => void;
  onStartAudit: () => void;
  onOpenBooking: () => void;
  onGoPricing: () => void;
}

const STARTER = TIERS.find((tier) => tier.id === 'starter')!;
const GROWTH = TIERS.find((tier) => tier.id === 'growth')!;
const SCALE = TIERS.find((tier) => tier.id === 'scale')!;
const ENTERPRISE = TIERS.find((tier) => tier.id === 'enterprise')!;

/**
 * The landing page's pricing cards: the two self-serve prices, plus the one free
 * way in. Scale and Enterprise get a one-line pointer rather than a card, because
 * both are quoted per deal and a card implies a checkout that does not exist for
 * them.
 *
 * ⚠️ **One free item, and there were two until 2026-08-10.** A "Free audit" card sat
 * immediately beside the sandbox, both reading `{sym}0`, so the ladder offered two
 * things that cost nothing and left the reader to work out which was which. Only the
 * sandbox is a plan — the Entry/Sandbox tier, per ADR-041. The audit is a motion, and
 * it keeps its own button in the header, the hero, the Modes section and the closing
 * band; it does not need a rung on a price ladder as well.
 *
 * ⚠️ **`onStartAudit` is still used and must stay.** Starter and Growth both open the
 * same modal, so removing the prop with the card would break the two paid CTAs.
 *
 * Every figure comes from data/plans.ts, which is also what the pricing page and
 * the assistant read — see that file's header for why nothing here is hardcoded.
 * The removed card's own copy was the one exception on this page, which is exactly
 * how it went on saying "for 30 days" after the product settled on seven; it now
 * lives in `FREE_AUDIT`.
 */
export function Pricing({
  ccyCode,
  ccySymbol,
  ccyName,
  taxLabel,
  ccyNote,
  onCycleCurrency,
  onStartAudit,
  onOpenBooking,
  onGoPricing,
}: PricingProps) {
  return (
    <Container id="pricing" className="land-section">
      <div className="land-section__head">
        <div>
          <h2 className="land-pricing__title">Priced on records under management, not seats</h2>
          <p className="land-section__lede">
            Shown in {ccyName}, excluding {taxLabel}. Every paid plan includes snapshots, rollback and
            the full audit trail.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onCycleCurrency}>
          Change currency · {ccyCode}
        </Button>
      </div>

      <div className="land-pricing__grid">
        <Card className="land-pricing__card">
          <div className="land-pricing__plan-name">Try it locally</div>
          <div className="land-pricing__price">
            <span className="land-pricing__amount">{ccySymbol}0</span>
            <span className="land-pricing__per-mo">free</span>
          </div>
          <div className="land-pricing__scope">
            runs in your browser · records never leave your device
          </div>
          {/* A link to the sandbox itself, not a form. This card says "free, runs in your
              browser, never leaves your device" — asking for an email address before honouring
              any of that contradicts the card it sits on. Same correction as the Modes
              section's; they were fixed at different times, which is why `PWA_URL` now has
              one owner in data/plans.ts. */}
          <LinkButton variant="secondary" size="md" href={PWA_URL} className="land-pricing__cta">
            Open the local sandbox
          </LinkButton>
        </Card>

        <Card className="land-pricing__card">
          <div className="land-pricing__plan-name">{STARTER.name}</div>
          <div className="land-pricing__price">
            <span className="land-pricing__amount">
              {ccySymbol}
              {formatAmount(STARTER.price ?? 0)}
            </span>
            <span className="land-pricing__per-mo">/mo</span>
          </div>
          <div className="land-pricing__scope">
            {STARTER.systems} · {STARTER.ceiling} · {STARTER.cadence}
          </div>
          <Button variant="secondary" size="md" className="land-pricing__cta" onClick={onStartAudit}>
            {STARTER.ctaLabel}
          </Button>
        </Card>

        <Card tone="accent" className="land-pricing__card land-pricing__card--featured">
          <div className="land-pricing__plan-head">
            <div className="land-pricing__plan-name">{GROWTH.name}</div>
            <span className="land-pricing__badge">Most teams</span>
          </div>
          <div className="land-pricing__price">
            <span className="land-pricing__amount">
              {ccySymbol}
              {formatAmount(GROWTH.price ?? 0)}
            </span>
            <span className="land-pricing__per-mo">/mo</span>
          </div>
          <div className="land-pricing__scope land-pricing__scope--on-accent">
            {GROWTH.systems} · {GROWTH.ceiling} · {GROWTH.cadence} · cross-system reconciliation
          </div>
          <Button variant="inverse" size="md" className="land-pricing__cta" onClick={onStartAudit}>
            {GROWTH.ctaLabel}
          </Button>
        </Card>
      </div>

      <div className="land-pricing__pointers">
        <button type="button" className="land-pricing__pointer" onClick={onGoPricing}>
          <span className="land-pricing__pointer-name">{SCALE.name}</span>
          <span className="land-pricing__pointer-price">
            {ccySymbol}
            {formatAmount(SCALE.price ?? 0)}/mo
          </span>
          <span className="land-pricing__pointer-scope">{SCALE.blurb}</span>
        </button>
        <button type="button" className="land-pricing__pointer" onClick={onOpenBooking}>
          <span className="land-pricing__pointer-name">{ENTERPRISE.name}</span>
          <span className="land-pricing__pointer-price">
            from {ccySymbol}
            {formatAmount(ENTERPRISE.price ?? 0)}/mo
          </span>
          <span className="land-pricing__pointer-scope">
            SSO, your own cloud, SOC 2-ready evidence packs
          </span>
        </button>
      </div>

      <div className="land-pricing__footer">
        <button type="button" className="land-pricing__compare" onClick={onGoPricing}>
          Compare all plans, add-ons and enterprise →
        </button>
        <div className="land-pricing__note">{ccyNote}</div>
      </div>
    </Container>
  );
}
